import { useScrollReveal } from '@/hooks/useScrollReveal';

const comparisonRows = [
  {
    dimension: 'Price Volatility',
    traditional: 'Highly seasonal. Farmers sell at low prices during harvest peak. Consumers pay 2-3x more in off-season.',
    freshhlock: 'Produce frozen at peak supply. Stable year-round pricing. Producers lock fair prices; consumers get predictable costs.',
    freshhlockIsBetter: true,
  },
  {
    dimension: 'Hygiene & Safety',
    traditional: 'Multiple touchpoints, ambient-temperature handling. No temperature audit trail. Spoilage often invisible until purchase.',
    freshhlock: 'HACCP-audited facilities. IQF or blast-frozen within hours of harvest/catch. Full cold-chain logging. No-questions replacement if cold chain breaks.',
    freshhlockIsBetter: true,
  },
  {
    dimension: 'Traceability',
    traditional: 'Opaque. You don\'t know where it came from, how it was stored, or who handled it along the way.',
    freshhlock: 'Batch-level traceability from harvest to delivery. QR codes on B2C orders. Full custody chain on B2B trades.',
    freshhlockIsBetter: true,
  },
  {
    dimension: 'Product Consistency',
    traditional: 'Every batch is different — size, cut, grade vary depending on who processed it and when.',
    freshhlock: 'Standard SKU specifications published upfront. Grade, cut, count/kg, pack size — all standardised. If it doesn\'t match, the trade doesn\'t clear.',
    freshhlockIsBetter: true,
  },
  {
    dimension: 'Capacity Access',
    traditional: 'Warehouse owners rely on personal networks. Transporters wait for calls. No marketplace, no price discovery.',
    freshhlock: 'Real-time capacity marketplace. Owners list idle space; buyers search and book. Prices discovered transparently.',
    freshhlockIsBetter: true,
  },
  {
    dimension: 'Payment & Settlement',
    traditional: 'Cash-heavy. Delayed payments. Credit based on personal trust, not institutional systems.',
    freshhlock: 'Platform-mediated settlement. Credit terms for verified HORECA accounts. Digital payment rails. Transparent fee structure.',
    freshhlockIsBetter: true,
  },
  {
    dimension: 'Scale Potential',
    traditional: 'Limited to local geography and personal networks. Hard to grow beyond your known catchment.',
    freshhlock: 'Pan-India reach from day one. List in Kochi, sell to Delhi. Network effect compounds with every new participant.',
    freshhlockIsBetter: true,
  },
];

export default function ComparisonTable() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100/70 border border-primary-200/50 mb-4">
            <span className="text-xs font-semibold text-primary-700 uppercase tracking-wider">The Difference</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-primary-700 mb-4">
            Traditional Chain vs. Freshhlock Network
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            The traditional fresh-market chain was built for a different era. Freshhlock was built for the one we live in.
          </p>
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block overflow-hidden rounded-2xl border border-background-200/60 shadow-sm">
          {/* Table header */}
          <div className="grid grid-cols-12 bg-brand-700">
            <div className="col-span-3 px-6 py-4">
              <span className="text-xs font-semibold text-brand-200 uppercase tracking-wider">Dimension</span>
            </div>
            <div className="col-span-4 px-6 py-4 border-l border-brand-600/50">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-brand-200 uppercase tracking-wider">Traditional Fresh-Market Chain</span>
              </div>
            </div>
            <div className="col-span-5 px-6 py-4 border-l border-brand-600/50">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-accent-400"></div>
                <span className="text-xs font-semibold text-paper-50 uppercase tracking-wider">Freshhlock Network</span>
              </div>
            </div>
          </div>

          {/* Table body */}
          {comparisonRows.map((row, idx) => (
            <div
              key={row.dimension}
              className={`grid grid-cols-12 border-b border-background-200/40 ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'} card-animate`}
            >
              <div className="col-span-3 px-6 py-5">
                <span className="font-heading text-sm font-semibold text-foreground-800">{row.dimension}</span>
              </div>
              <div className="col-span-4 px-6 py-5 border-l border-background-200/30">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-line text-xs text-red-500"></i>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{row.traditional}</p>
                </div>
              </div>
              <div className="col-span-5 px-6 py-5 border-l border-background-200/30 bg-accent-50/30">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-paper-50"></i>
                  </div>
                  <p className="text-xs text-foreground-700 leading-relaxed font-medium">{row.freshhlock}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden space-y-4">
          {comparisonRows.map((row) => (
            <div key={row.dimension} className="bg-card-50 rounded-xl border border-card-200 overflow-hidden shadow-sm card-animate">
              <div className="px-5 py-3 bg-primary-50/50 border-b border-primary-100/30">
                <span className="font-heading text-sm font-semibold text-primary-700">{row.dimension}</span>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-line text-xs text-red-500"></i>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">Traditional</span>
                    <p className="text-xs text-foreground-500 leading-relaxed mt-0.5">{row.traditional}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-paper-50"></i>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-accent-600 uppercase tracking-wider">Freshhlock</span>
                    <p className="text-xs text-foreground-700 leading-relaxed font-medium mt-0.5">{row.freshhlock}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}